import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import HistoryIcon from '@mui/icons-material/History'
import StarIcon from '@mui/icons-material/Star'
import CoPresentIcon from '@mui/icons-material/CoPresent'
import { formatDate, interestColor, stageMeta, INTEREST_LABELS } from './helpers'

const softChipSx = (color) => ({
  bgcolor: alpha(color, 0.12),
  color,
  fontWeight: 700,
  borderRadius: 1.5,
  '& .MuiChip-icon': { color },
})

export const StageChip = ({ stage, size = 'small', sx }) => {
  const meta = stageMeta(stage)
  return (
    <Chip
      label={meta.label}
      size={size}
      sx={{ ...softChipSx(meta.color), ...sx }}
    />
  )
}

// Ex-trial marker: the organisation once signed up for a trial, so they have
// seen the product and are worth a follow-up.
export const TrialledChip = ({ trialEnd, size = 'small', sx }) => (
  <Tooltip
    title={trialEnd ? `Trial ended ${formatDate(trialEnd)}` : 'Previously trialled AmbuNet'}
  >
    <Chip
      icon={<HistoryIcon />}
      label={trialEnd ? `Trialled · ${formatDate(trialEnd)}` : 'Trialled'}
      size={size}
      sx={{ ...softChipSx('#557A95'), ...sx }}
    />
  </Tooltip>
)

// Per-lead demo flag: they have been shown the product on a call / in person.
// Not a pipeline stage - a lead at any stage can carry it.
export const DemoChip = ({ demoAt, size = 'small', sx }) => (
  <Tooltip title={demoAt ? `Demo given ${formatDate(demoAt)}` : 'Has had a demo'}>
    <Chip
      icon={<CoPresentIcon />}
      label={demoAt ? `Demo · ${formatDate(demoAt)}` : 'Demo'}
      size={size}
      sx={{ ...softChipSx('#85276A'), ...sx }}
    />
  </Tooltip>
)

export const InterestChip = ({ level, size = 'small', sx }) => {
  const value = Number(level)
  if (!value) {
    return (
      <Chip
        label="Interest unknown"
        size={size}
        variant="outlined"
        sx={{ borderRadius: 1.5, color: 'text.secondary', ...sx }}
      />
    )
  }
  const color = interestColor(value)
  return (
    <Chip
      icon={<StarIcon />}
      label={`${INTEREST_LABELS[value] ?? value} · ${value}/5`}
      size={size}
      sx={{ ...softChipSx(color), ...sx }}
    />
  )
}

export const SoftChip = ({ color = '#64748B', label, icon, size = 'small', sx, ...props }) => (
  <Chip
    icon={icon}
    label={label}
    size={size}
    sx={{ ...softChipSx(color), ...sx }}
    {...props}
  />
)
